import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CAMERA_SETTINGS } from '../constants/gameSettings.js';

export default class CameraManager {
    constructor(camera, renderer, scene) {
        this.camera = camera;
        this.renderer = renderer;
        this.scene = scene;
        
        // Camera settings
        this.followCameraEnabled = true;
        this.cameraHeight = CAMERA_SETTINGS.HEIGHT;
        this.cameraDistance = CAMERA_SETTINGS.DISTANCE;
        this.cameraLookAhead = 5;
        this.followLookInput = { x: 0, y: 0 };
        this.followLookYaw = 0;
        this.followLookPitch = 0;
        this.followLookYawMax = 0.95;
        this.followLookPitchMax = 0.7;
        this.followLookYawSpeed = 2.3;
        this.followLookPitchSpeed = 2.0;
        this.followLookReturnSpeed = 3.0;
        this.followLookResponseExpo = 1.65;
        
        // Camera controls
        this.controls = null;
        
        // For camera transitions
        this.transitionParams = {
            active: false,
            startTime: 0,
            duration: 0,
            startPosition: new THREE.Vector3(),
            targetPosition: new THREE.Vector3(),
            startTarget: new THREE.Vector3(),
            endTarget: new THREE.Vector3()
        };
    }
    
    setupCameraControls() {
        // Set initial camera position
        this.camera.position.set(0, 15, 30);
        this.camera.lookAt(0, 0, 0);
        
        // Initialize orbit controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        
        // Enhanced controls configuration
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.minDistance = 5;
        this.controls.maxDistance = 200;
        this.controls.maxPolarAngle = Math.PI / 2 - 0.1; // Prevent going below ground
        this.controls.screenSpacePanning = false;
        this.controls.rotateSpeed = 0.5;
        this.controls.zoomSpeed = 1.0;
        this.controls.panSpeed = 0.8;
        
        // Disable initially if using follow camera
        this.controls.enabled = !this.followCameraEnabled;
        
        return this.controls;
    }
    
    toggleCameraMode() {
        this.followCameraEnabled = !this.followCameraEnabled;
        
        // Enable/disable orbit controls based on camera mode
        if (this.controls) {
            this.controls.enabled = !this.followCameraEnabled;
        }
        
        // If switching to orbit camera, set a good initial position
        if (!this.followCameraEnabled) {
            // Store current position and target for smooth transition
            this.transitionParams.active = true;
            this.transitionParams.startTime = performance.now();
            this.transitionParams.duration = 1000; // 1 second
            this.transitionParams.startPosition.copy(this.camera.position);
            
            // Calculate a good position for orbit camera
            if (this.targetObject && this.targetObject.mesh) {
                // Target is the bus position
                const targetPos = this.targetObject.mesh.position.clone();
                
                // End position is at an angle from the bus
                const distance = 30;
                const height = 20;
                const angle = this.targetObject.mesh.rotation.y + Math.PI; // Behind the bus
                
                const endPos = new THREE.Vector3(
                    targetPos.x + Math.sin(angle) * distance,
                    targetPos.y + height,
                    targetPos.z + Math.cos(angle) * distance
                );
                
                this.transitionParams.targetPosition.copy(endPos);
                this.transitionParams.startTarget.copy(this.controls.target);
                this.transitionParams.endTarget.copy(targetPos);
            }
        } else {
            // Switching to follow camera, reset camera height and distance
            this.cameraHeight = CAMERA_SETTINGS.HEIGHT;
            this.cameraDistance = CAMERA_SETTINGS.DISTANCE;
            
            // Clear any active transitions
            this.transitionParams.active = false;
        }
        
        return this.followCameraEnabled ? 'Follow' : 'Orbit';
    }
    
    resetCameraView() {
        if (!this.followCameraEnabled && this.controls && this.targetObject && this.targetObject.mesh) {
            // Reset orbit controls to look at the bus from a good angle
            const targetPos = this.targetObject.mesh.position.clone();
            
            // Store current position for transition
            this.transitionParams.active = true;
            this.transitionParams.startTime = performance.now();
            this.transitionParams.duration = 1000; // 1 second
            this.transitionParams.startPosition.copy(this.camera.position);
            
            // Calculate a good position
            const distance = 30;
            const height = 20;
            const angle = this.targetObject.mesh.rotation.y + Math.PI; // Behind the bus
            
            const newPos = new THREE.Vector3(
                targetPos.x + Math.sin(angle) * distance,
                targetPos.y + height,
                targetPos.z + Math.cos(angle) * distance
            );
            
            this.transitionParams.targetPosition.copy(newPos);
            this.transitionParams.startTarget.copy(this.controls.target);
            this.transitionParams.endTarget.copy(targetPos);
        }
    }
    
    setTarget(target) {
        this.targetObject = target;
    }

    setFollowLookInput(x = 0, y = 0) {
        this.followLookInput.x = x;
        this.followLookInput.y = y;
    }

    applyLookResponseCurve(value) {
        const sign = Math.sign(value);
        const magnitude = Math.abs(value);
        if (magnitude < 0.0001) return 0;
        return sign * Math.pow(magnitude, this.followLookResponseExpo);
    }
    
    updateCamera(deltaTime) {
        // Handle camera transitions
        if (this.transitionParams.active) {
            const elapsed = performance.now() - this.transitionParams.startTime;
            const progress = Math.min(elapsed / this.transitionParams.duration, 1.0);
            
            // Use easing function for smoother transition
            const eased = this.easeInOutQuad(progress);
            
            // Interpolate camera position
            this.camera.position.lerpVectors(
                this.transitionParams.startPosition,
                this.transitionParams.targetPosition,
                eased
            );
            
            // Interpolate target position
            if (this.controls) {
                this.controls.target.lerpVectors(
                    this.transitionParams.startTarget,
                    this.transitionParams.endTarget,
                    eased
                );
                this.controls.update();
            }
            
            // End transition when complete
            if (progress >= 1.0) {
                this.transitionParams.active = false;
            }
        }
        
        // Update follow camera
        if (this.followCameraEnabled && this.targetObject && this.targetObject.mesh) {
            const bus = this.targetObject;
            const lookInputX = this.applyLookResponseCurve(this.followLookInput?.x || 0);
            const lookInputY = this.applyLookResponseCurve(this.followLookInput?.y || 0);
            const hasLookInput = Math.abs(lookInputX) > 0.02 || Math.abs(lookInputY) > 0.02;

            if (hasLookInput) {
                this.followLookYaw -= lookInputX * this.followLookYawSpeed * deltaTime;
                this.followLookPitch -= lookInputY * this.followLookPitchSpeed * deltaTime;
            } else {
                const returnLerp = Math.min(1, deltaTime * this.followLookReturnSpeed);
                this.followLookYaw = THREE.MathUtils.lerp(this.followLookYaw, 0, returnLerp);
                this.followLookPitch = THREE.MathUtils.lerp(this.followLookPitch, 0, returnLerp);
            }

            this.followLookYaw = THREE.MathUtils.clamp(this.followLookYaw, -this.followLookYawMax, this.followLookYawMax);
            this.followLookPitch = THREE.MathUtils.clamp(this.followLookPitch, -this.followLookPitchMax, this.followLookPitchMax);
            
            // Get bus position and rotation
            const busPos = bus.mesh.position.clone();
            const busDir = new THREE.Vector3(0, 0, -1).applyQuaternion(bus.mesh.quaternion);
            const lookDir = busDir.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), this.followLookYaw);
            
            // Calculate camera position behind the bus
            const cameraOffset = lookDir.clone().multiplyScalar(-this.cameraDistance);
            cameraOffset.y = this.cameraHeight + (this.followLookPitch * 6);
            
            const targetCameraPos = busPos.clone().add(cameraOffset);
            
            // Smoothly move camera to target position using CAMERA_SETTINGS.SMOOTH_FACTOR
            this.camera.position.lerp(targetCameraPos, deltaTime * (1 / CAMERA_SETTINGS.SMOOTH_FACTOR));

            // Clamp camera above terrain so it never clips through hills
            const heightMap = this.scene?.userData?.terrainHeightMap;
            if (heightMap) {
                const minCamY = heightMap.getHeight(this.camera.position.x, this.camera.position.z) + 3;
                if (this.camera.position.y < minCamY) {
                    this.camera.position.y = minCamY;
                }
            }

            // Calculate look target in front of the bus
            const lookAheadOffset = lookDir.clone().multiplyScalar(this.cameraLookAhead);
            const targetLookAt = busPos.clone().add(lookAheadOffset);
            
            // Set higher look-at position for better visibility
            targetLookAt.y = busPos.y + 1.0 + (this.followLookPitch * 2);
            
            // Point camera at target
            this.camera.lookAt(targetLookAt);
        } else if (!this.transitionParams.active && this.controls) {
            // Update orbit controls
            this.controls.update();
            this.followLookYaw = THREE.MathUtils.lerp(this.followLookYaw, 0, Math.min(1, deltaTime * this.followLookReturnSpeed));
            this.followLookPitch = THREE.MathUtils.lerp(this.followLookPitch, 0, Math.min(1, deltaTime * this.followLookReturnSpeed));
        }
    }
    
    // Easing function for smooth transitions
    easeInOutQuad(t) {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }
    
    onWindowResize(width, height) {
        // Update camera aspect ratio
        if (this.camera instanceof THREE.PerspectiveCamera) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }
    }
} 
